import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpDown, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAIProvider } from '@/hooks/useAIProvider';
import { capitalizeWords, constants } from '@/lib';
import { CreateIncident, IncidentCard } from '@/lib/modules/incidents';
import { Badge, Nodata, Button, PTTIcon, StatusDot, StatusTabs, Searchbar } from '..';
import { useTranslation } from 'react-i18next';

export const IncidentList = ({ incidents, responders, onRowClick, onExpandedChange }) => {
  const { callTranscribeAudio } = useAIProvider({ temperature: 0.6 });
  const { t } = useTranslation();
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState([]);
  const [activeStatusTab, setActiveStatusTab] = useState('all');
  const [columnFilters, setColumnFilters] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [showCreateIncident, setShowCreateIncident] = useState(false);

  // PTT state for incidents
  const [activePttIncident, setActivePttIncident] = useState(null);
  const [isPttRecording, setIsPttRecording] = useState(false);

  // PTT Functions for Incidents
  const startPtt = async incident => {
    if (!incident || isPttRecording) return;

    try {
      setActivePttIncident(incident);
      setIsPttRecording(true);

      await wbSdk.sipManager.openPtt('incident', incident.id, incident.title, false);
    } catch (error) {
      console.error('Error starting PTT for incident:', error);
      setIsPttRecording(false);
      setActivePttIncident(null);
    }
  };

  const stopPtt = async () => {
    if (!isPttRecording || !activePttIncident) return;

    try {
      setIsPttRecording(false);

      // Close PTT session in SDK and get audio data
      const audioData = await wbSdk.sipManager.closePtt(activePttIncident.id);

      if (audioData && audioData.blob) {
        await handleSendPttAudio(audioData.blob, activePttIncident);
      }

      setActivePttIncident(null);
    } catch (error) {
      console.error('Error stopping PTT:', error);
      setIsPttRecording(false);
      setActivePttIncident(null);
    }
  };

  const handleSendPttAudio = async (audioBlob, incident) => {
    try {
      if (!incident || !audioBlob) return;

      let transcription = '';

      try {
        transcription = await transcribeAudio(audioBlob);
      } catch (transcriptionError) {
        console.warn('Transcription failed, sending audio without text:', transcriptionError);
      }

      const incidentChat = await wbSdk.chatManager.getChatBox(`incident_${incident.id}`, true);

      const audioFile = new File([audioBlob], `ptt-audio-${Date.now()}.webm`, {
        type: audioBlob.type,
      });

      await incidentChat.sendMessage(transcription, [audioFile]);
    } catch (error) {
      console.error('Error sending PTT audio for incident:', error);
    }
  };

  const transcribeAudio = async audioBlob => {
    try {
      const audioFile = new File([audioBlob], 'audio.webm', {
        type: 'audio/webm',
      });

      const response = await callTranscribeAudio(audioFile, {
        model: 'whisper-1',
        language: 'en',
        response_format: 'text',
      });

      return response;
    } catch (error) {
      console.error('OpenAI transcription error:', error);
      throw error;
    }
  };

  // PTT Icon click handler for incidents
  const handlePttClick = (incident, event) => {
    event.stopPropagation();

    if (isPttRecording && activePttIncident?.id === incident.id) {
      stopPtt();
    } else if (!isPttRecording) {
      startPtt(incident);
    }
  };

  const isPttActiveForIncident = incident => {
    return isPttRecording && activePttIncident?.id === incident.id;
  };

  const columns = [
    {
      header: capitalizeWords(t('INCIDENTS_PANEL.INCIDENT_NAME')),
      accessorKey: 'title',
      cell: ({ row }) => {
        return (
          <span className="flex flex-row">{`${row.original.serial_id} - ${
            t('INCIDENT_TYPES', { returnObjects: true }).find(type => type.value === row.getValue('type'))?.label ||
            row.getValue('type')
          }`}</span>
        );
      },
    },
    {
      header: capitalizeWords(t('INCIDENTS_PANEL.INCIDENT_TYPE')),
      accessorKey: 'type',
      cell: info => {
        return (
          <span className="flex flex-row">
            {t('INCIDENT_TYPES', { returnObjects: true }).find(type => type.value === info.getValue())?.label ||
              info.getValue()}
          </span>
        );
      },
    },
    {
      header: capitalizeWords(constants.INCIDENTS_PANEL.ASSIGNEES),
      accessorKey: 'assignees',
      accessorFn: row => {
        if (!row.assignees || row.assignees.length === 0) return '—';
        return row.assignees.map(a => a.display_name).join(' ');
      },
      cell: info => {
        const assignees = info.row.original.assignees || [];
        if (assignees.length === 0) {
          return <span className="text">—</span>;
        }

        const first = assignees[0].display_name;
        const extra = assignees.length - 1;

        return (
          <span>
            {first}{' '}
            {extra > 0 && (
              <span
                title={assignees
                  .slice(1)
                  .map(a => a.display_name)
                  .join(', ')}
              >
                +{extra}
              </span>
            )}
          </span>
        );
      },
    },
    {
      accessorKey: 'severity',
      header: ({ column }) => {
        return (
          <button
            className="flex flex-row items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            {capitalizeWords(constants.INCIDENTS_PANEL.SEVERITY)}
            <ArrowUpDown size={12} />
          </button>
        );
      },
      cell: ({ row }) => {
        const val = row.getValue('severity');

        let label, color;
        switch (val) {
          case 0:
            label = capitalizeWords(constants.INCIDENTS_PANEL.SEVERITY_MINIMAL);
            color = 'bg-icon-muted';
            break;
          case 1:
            label = capitalizeWords(constants.INCIDENTS_PANEL.SEVERITY_LOW);
            color = 'bg-interactive';
            break;
          case 2:
            label = capitalizeWords(constants.INCIDENTS_PANEL.SEVERITY_MEDIUM);
            color = 'bg-chart-4';
            break;
          case 3:
            label = capitalizeWords(constants.INCIDENTS_PANEL.SEVERITY_HIGH);
            color = 'bg-chart-2';
            break;
          case 4:
            label = capitalizeWords(constants.INCIDENTS_PANEL.SEVERITY_CRITICAL);
            color = 'bg-icon-error';
            break;
        }

        return (
          <div className="flex items-center gap-2">
            <StatusDot className={color} />
            <span>{label}</span>
          </div>
        );
      },
      enableSorting: true,
    },
    {
      header: ({ column }) => {
        return (
          <button
            className="flex flex-row items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            {capitalizeWords(constants.INCIDENTS_PANEL.STATUS)}
            <ArrowUpDown size={12} />
          </button>
        );
      },
      accessorKey: 'status',
      cell: ({ row }) => {
        let variant;
        switch (row.getValue('status')) {
          case 'created':
          case 'progress':
            variant = 'SurfaceRed';
            break;
          case 'resolved':
          case 'closed':
            variant = 'SurfaceGreen';
            break;
          default:
            variant = '';
        }
        return (
          <Badge variant={variant} size="sm" className="text-xs">
            {/* {capitalizeWords(row.getValue('status'))} */}
            {
              t('INCIDENT_CARD_STATUS_VALUES', { returnObjects: true }).find(
                item => item.value === row.getValue('status')
              )?.label
            }
          </Badge>
        );
      },
      enableSorting: true,
      filterFn: (row, columnId, filterValue) => {
        const status = row.getValue(columnId);
        if (Array.isArray(filterValue)) {
          return filterValue.includes(status);
        }
        return status === filterValue;
      },
    },
    {
      id: 'call',
      header: constants.INCIDENTS_PANEL.PTT,
      enableHiding: false,
      cell: ({ row }) => {
        const incident = row.original;
        const isPttActive = isPttActiveForIncident(incident);

        return (
          <span className="flex flex-row gap-1">
            <PTTIcon isDisabled={false} isActive={isPttActive} onClick={e => handlePttClick(incident, e)} />
          </span>
        );
      },
    },
    {
      accessorKey: 'location',
      header: capitalizeWords(constants.INCIDENTS_PANEL.LOCATION),
      enableHiding: true,
      cell: ({ row }) => {
        return <span className="flex flex-row">{row.getValue('location').display_name}</span>;
      },
    },
  ];

  // allow only single row expanded
  const handleExpandedChange = updater => {
    const next = typeof updater === 'function' ? updater(expanded) : updater;
    const nextKeys = Object.keys(next);
    const prevKeys = Object.keys(expanded);

    // Detect the key being toggled
    const toggledKey = nextKeys.find(k => !prevKeys.includes(k)) || prevKeys.find(k => !nextKeys.includes(k));

    if (!toggledKey) {
      setExpanded({});
      if (onExpandedChange) onExpandedChange(null);
      return;
    }

    const isOpening = !!next[toggledKey];
    const newExpanded = isOpening ? { [toggledKey]: true } : {};

    setExpanded(newExpanded);

    // Notify parent component about the expanded state change
    if (onExpandedChange) {
      onExpandedChange(isOpening ? toggledKey : null);
    }
  };

  const table = useReactTable({
    data: incidents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    state: {
      sorting,
      globalFilter,
      columnVisibility: { location: false },
      columnFilters,
      expanded,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: () => {},
    onColumnFiltersChange: setColumnFilters,
    onExpandedChange: handleExpandedChange,
    sortingFns: 'basic',
    globalFilterFn: 'includesString',
  });

  const tabCounts = useMemo(() => {
    const counts = {
      all: incidents.length,
      active: 0,
      closed: 0,
    };

    const activeStatuses = [
      constants.TAB_KEYS.TAB_VALUE_UNACKNOWLEDGED,
      constants.TAB_KEYS.TAB_VALUE_CREATED,
      constants.TAB_KEYS.TAB_VALUE_PROGRESS,
    ];

    const closedStatuses = [constants.TAB_KEYS.CLOSED, constants.TAB_KEYS.TAB_VALUE_RESOLVED];

    incidents.forEach(incident => {
      if (activeStatuses.includes(incident.status)) counts.active++;
      if (closedStatuses.includes(incident.status)) counts.closed++;
    });
    return counts;
  }, [incidents]);

  const handleTabChange = tabKey => {
    setActiveStatusTab(tabKey);

    if (tabKey === constants.TAB_KEYS.ALL) {
      table.getColumn('status')?.setFilterValue(undefined);
    } else if (tabKey === constants.TAB_KEYS.ACTIVE) {
      table
        .getColumn('status')
        ?.setFilterValue([
          constants.TAB_KEYS.TAB_VALUE_UNACKNOWLEDGED,
          constants.TAB_KEYS.TAB_VALUE_CREATED,
          constants.TAB_KEYS.TAB_VALUE_PROGRESS,
        ]);
    } else if (tabKey === constants.TAB_KEYS.CLOSED) {
      table.getColumn('status')?.setFilterValue([constants.TAB_KEYS.CLOSED, constants.TAB_KEYS.TAB_VALUE_RESOLVED]);
    }
  };

  const tabs = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'closed', label: 'Closed' },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden bg">
      <div className="flex flex-row justify-between p-3 gap-2">
        {/* Tabs */}
        <StatusTabs tabs={tabs} activeTab={activeStatusTab} onTabChange={handleTabChange} dataCount={tabCounts} />
        <Button className="p-3" variant="default" size="icon" onClick={() => setShowCreateIncident(true)}>
          <Plus size={'1rem'} />
        </Button>
      </div>
      <div className="px-3 pl-3 pr-3 pb-3">
        <Searchbar
          value={globalFilter}
          onChange={val => {
            setGlobalFilter(val);
          }}
        />
      </div>
      <div className="min-w-[30rem] flex flex-col flex-1 overflow-hidden overflow-x-auto">
        {/* Header */}
        <div className="grid grid-cols-[repeat(5,1fr)_auto] items-center text-xs font-normal text-muted py-3 pl-6 pr-10 border-t border-b">
          {table.getHeaderGroups().map(headerGroup => (
            <div key={headerGroup.id} className="contents">
              {headerGroup.headers
                .filter(header => !header.column.columnDef.meta?.hidden)
                .map(header => (
                  <span key={header.id} className="flex flex-row items-center">
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </span>
                ))}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto relative">
          <div className="space-y-2 p-3">
            <AnimatePresence initial={false}>
              {showCreateIncident && (
                <CreateIncident handleFormCancel={() => setShowCreateIncident(false)} responders={responders} />
              )}
            </AnimatePresence>
            {table?.getRowModel()?.rows.length > 0 ? (
              table
                .getRowModel()
                .rows.map(row => (
                  <IncidentCard key={row.id} cardData={row} responders={responders} onRowClick={onRowClick} />
                ))
            ) : (
              <div
                className={clsx(
                  'flex items-center justify-center py-10 text-sm text-muted-foreground border rounded-lg',
                  !showCreateIncident && 'h-full'
                )}
              >
                <Nodata title={`${t('FALLBACK_SCREEN.DATA_FOUND')}`} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
